/* eslint-disable @typescript-eslint/no-invalid-void-type */
/**
 * Per-account Voice profile hooks.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { getJson, patchJson, postJson } from './_http';

/* ─── Voices — Wave 5b Agent 5b.3 (FULLY IMPLEMENTED) ──────────────────── */
//
// `voice.linkedinAccountId` keys the per-account profile. Hooks consume
// the typed shapes below; the API mutates the row in place + returns the
// fresh value, so we surface that directly to the React-query cache.

export interface VoiceRow {
  id: string;
  userId: string;
  linkedinAccountId: string;
  tone: 'formal' | 'semi' | 'casual';
  length: 'short' | 'medium' | 'mirror';
  humor: 'never' | 'occasional' | 'natural';
  emoji: 'never' | 'light' | 'yes';
  language: 'fr' | 'en' | 'mirror';
  formality: 'vous' | 'tu' | 'mirror';
  adaptive: boolean;
  banned: string[];
  signature: string[];
  weekendSend: boolean;
  nightSend: boolean;
  nightStartHour: number;
  nightEndHour: number;
  notifyHotEnabled: boolean;
  notifyHotAction: 'auto' | 'takeover' | 'validate';
  notifyReviewEnabled: boolean;
  notifyRecapEnabled: boolean;
  notifyRecapHour: string;
  notifyChannel: 'whatsapp' | 'telegram' | 'slack' | 'email';
  notifyWhatsapp: string | null;
  notifyTelegram: string | null;
  notifySlack: string | null;
  notifyEmail: string | null;
  notifyReplyEnabled: boolean;
  lastAnalysedAt: string | null;
  lastSuggestion: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface VoiceAccountSummary {
  id: string;
  name: string;
  initials: string;
  color: string;
  avatarUrl: string | null;
  status: string;
  aiMode: 'auto' | 'review' | 'manual';
  historyEligible: boolean;
  historyConvs: number | null;
  historySpan: string | null;
  timezone: string;
  /** Wave B3 — Mode IA section in the voice editor (US-085, US-086, US-087). */
  showOffTarget: boolean | null;
  offTargetMode: 'block' | 'suggest' | 'manual';
  autoLeadDetect: boolean | null;
}

export interface VoiceResponse {
  voice: VoiceRow;
  account: VoiceAccountSummary;
  notificationChannels: NotificationChannelCapabilities;
}

export type VoiceNotificationChannel = VoiceRow['notifyChannel'];
export type NotificationUnavailableReason =
  | 'provider_not_configured'
  | 'slack_bot_token_not_configured'
  | 'whatsapp_sender_id_not_configured';
export type NotificationChannelCapabilities = Record<
  VoiceNotificationChannel,
  {
    available: boolean;
    hotLeadAvailable: boolean;
    reviewAvailable: boolean;
    dailyRecapAvailable: boolean;
    recapHourConfigurable: boolean;
    /** True only when inbound provider commands are actually supported. */
    repliesAvailable: boolean;
    reason: NotificationUnavailableReason | null;
  }
>;

export type VoicePatch = Partial<
  Omit<VoiceRow, 'id' | 'userId' | 'linkedinAccountId' | 'createdAt' | 'updatedAt'>
>;

export interface AnalyseHistoryResponse {
  workflowId: string;
  convsAnalysed: number;
  span: string | null;
  startedAt: string;
}

export interface VoiceSuggestionPatch {
  tone?: VoiceRow['tone'];
  length?: VoiceRow['length'];
  humor?: VoiceRow['humor'];
  emoji?: VoiceRow['emoji'];
  language?: VoiceRow['language'];
  formality?: VoiceRow['formality'];
  adaptive?: boolean;
  banned?: string[];
  signature?: string[];
  convsAnalysed?: number;
  span?: string;
  notes?: string;
}

const voiceKey = (linkedinAccountId: string) => ['voice', linkedinAccountId] as const;

/** `GET /api/v1/voice/:linkedinAccountId` */
export function useVoice(
  linkedinAccountId: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<VoiceResponse, ApiError> {
  const client = useApiClient();
  return useQuery<VoiceResponse, ApiError>({
    queryKey: linkedinAccountId ? voiceKey(linkedinAccountId) : ['voice', '__none__'],
    queryFn: () =>
      getJson<VoiceResponse>(
        client,
        `/api/v1/voice/${encodeURIComponent(linkedinAccountId ?? '')}`,
      ),
    enabled: !!linkedinAccountId && (options.enabled ?? true),
    retry: false,
  });
}

/** `PATCH /api/v1/voice/:linkedinAccountId` */
export function useUpdateVoice(
  linkedinAccountId: string,
): UseMutationResult<{ voice: VoiceRow }, ApiError, VoicePatch> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ voice: VoiceRow }, ApiError, VoicePatch>({
    mutationFn: (patch) =>
      patchJson<{ voice: VoiceRow }>(
        client,
        `/api/v1/voice/${encodeURIComponent(linkedinAccountId)}`,
        patch,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: voiceKey(linkedinAccountId) });
    },
  });
}

/** `POST /api/v1/voice/:linkedinAccountId/analyze-history` (Wave 6 will fan-out via WS). */
export function useAnalyzeHistory(
  linkedinAccountId: string,
): UseMutationResult<AnalyseHistoryResponse, ApiError, void> {
  const client = useApiClient();
  return useMutation<AnalyseHistoryResponse, ApiError, void>({
    mutationFn: () =>
      postJson<AnalyseHistoryResponse>(
        client,
        `/api/v1/voice/${encodeURIComponent(linkedinAccountId)}/analyze-history`,
        {},
      ),
  });
}

// Historical alias (kept exported per the original stub contract).
export const useAnalyseVoice = useAnalyzeHistory;

/** `POST /api/v1/voice/:linkedinAccountId/apply-suggestions` */
export function useApplySuggestions(
  linkedinAccountId: string,
): UseMutationResult<
  { voice: VoiceRow; applied: number },
  ApiError,
  { patch: VoiceSuggestionPatch }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { voice: VoiceRow; applied: number },
    ApiError,
    { patch: VoiceSuggestionPatch }
  >({
    mutationFn: (vars) =>
      postJson<{ voice: VoiceRow; applied: number }>(
        client,
        `/api/v1/voice/${encodeURIComponent(linkedinAccountId)}/apply-suggestions`,
        vars,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: voiceKey(linkedinAccountId) });
    },
  });
}

/** `POST /api/v1/voice/:linkedinAccountId/notify-test` — resolves after provider acceptance. */
export function useNotifyTest(
  linkedinAccountId: string,
): UseMutationResult<
  { ok: true; channel: VoiceNotificationChannel; sentAt: string },
  ApiError,
  void
> {
  const client = useApiClient();
  return useMutation<
    { ok: true; channel: VoiceNotificationChannel; sentAt: string },
    ApiError,
    void
  >({
    mutationFn: () =>
      postJson<{ ok: true; channel: VoiceNotificationChannel; sentAt: string }>(
        client,
        `/api/v1/voice/${encodeURIComponent(linkedinAccountId)}/notify-test`,
        {},
      ),
  });
}
